import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { Workspace } from "@workcore/schemas";
import { api, checkedWrite, get } from "../api";
import { ErrorBox, Field, PageHeader, Panel, RowMenu, SidePanel, StatusBadge, When, when } from "../ui";
import { confirmAction } from "../confirm";

const WorkspaceList = z.array(Workspace);
const busy = (s: string) => s === "requested" || s === "provisioning";

export function useWorkspaces() {
  return useQuery({
    queryKey: ["workspaces"],
    queryFn: async () => WorkspaceList.parse(await get("/v1/admin/workspaces")),
    // Poll while anything is still being provisioned, so status changes appear on their own.
    refetchInterval: (q) => (q.state.data?.some((w) => busy(w.status) || (w.status === "failed" && w.attempts < 5)) ? 2000 : false),
  });
}

export function WorkspaceActions({ w }: { w: Workspace }) {
  const queries = useQueryClient();
  const act = useMutation({
    mutationFn: (action: "retry" | "archive" | "unarchive") => checkedWrite(`POST`, `/v1/admin/workspaces/${w.slug}/${action}`),
    onSuccess: () => queries.invalidateQueries({ queryKey: ["workspaces"] }),
  });
  return (
    <>
      <RowMenu
        label={`Actions for ${w.name}`}
        items={[
          w.status === "failed" && { label: "Retry", onSelect: () => act.mutate("retry") },
          w.status === "archived" && { label: "Unarchive", onSelect: () => act.mutate("unarchive") },
          w.status === "active" && {
            label: "Archive…",
            danger: true,
            onSelect: () => void confirmAction({ title: `Archive workspace '${w.name}'?`, body: "It stops being served until you unarchive it. Nothing is deleted.", action: "Archive", check: () => api("POST", `/v1/admin/workspaces/${w.slug}/archive`, { dryRun: true }) }).then((ok) => ok && act.mutate("archive")),
          },
        ]}
      />
      {act.error ? <ErrorBox error={act.error} /> : null}
    </>
  );
}

function NewWorkspace({ open, onDone }: { open: boolean; onDone: () => void }) {
  const queries = useQueryClient();
  const templates = useQuery({ queryKey: ["starter-templates"], queryFn: () => get<string[]>("/v1/admin/starter-templates") });
  const [slug, setSlug] = useState("");
  const [name, setName] = useState("");
  const [admin, setAdmin] = useState("");
  const [chosen, setChosen] = useState<string[]>([]);
  const create = useMutation({
    mutationFn: () =>
      checkedWrite<unknown>("POST", "/v1/admin/workspaces", {
        slug,
        name,
        ...(admin ? { initialAdminEmail: admin } : {}),
        ...(chosen.length ? { starterTemplates: chosen } : {}),
      }),
    onSuccess: () => {
      void queries.invalidateQueries({ queryKey: ["workspaces"] });
      setSlug(""), setName(""), setAdmin(""), setChosen([]);
      onDone();
    },
  });
  return (
    <SidePanel title="New workspace" open={open} onClose={onDone} onSubmit={() => create.mutate()} submitLabel={create.isPending ? "Requesting…" : "Request workspace"} busy={create.isPending} error={create.error}>
      <Field label="Slug" help="Lower-case letters, digits and hyphens. Part of URLs and the database name; it cannot change.">
        <input value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())} placeholder="acme" required pattern="[a-z][a-z0-9-]{1,39}" autoFocus />
      </Field>
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Ltd" required />
      </Field>
      <Field label="First system admin (email)" help="Invited as member and system admin; accepted when they first sign in with this email. Optional.">
        <input type="email" value={admin} onChange={(e) => setAdmin(e.target.value)} />
      </Field>
      <fieldset className="field-set">
        <legend>Starter workflows</legend>
        {templates.data?.map((t) => (
          <label key={t} className="check">
            <input type="checkbox" checked={chosen.includes(t)} onChange={(e) => setChosen(e.target.checked ? [...chosen, t] : chosen.filter((x) => x !== t))} />
            {t}
          </label>
        ))}
        {templates.data?.length === 0 && <p className="muted small">No starter templates installed.</p>}
      </fieldset>
    </SidePanel>
  );
}

export function WorkspacesPage() {
  const list = useWorkspaces();
  const [adding, setAdding] = useState(false);
  return (
    <>
      <PageHeader title="Workspaces" description="Each workspace has its own database. New ones are set up in the background; the status here updates as that happens." actions={<button onClick={() => setAdding(true)}>New workspace</button>} />
      <NewWorkspace open={adding} onDone={() => setAdding(false)} />
      <ErrorBox error={list.error} />
      <table className="grid">
        <thead>
          <tr>
            <th>Name</th>
            <th>Slug</th>
            <th>Status</th>
            <th className="opt">Schema</th>
            <th>Requested</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {list.data?.map((w) => (
            <tr key={w.id}>
              <td>
                <Link to={`/platform/workspaces/${w.slug}`}>{w.name}</Link>
              </td>
              <td>
                <code>{w.slug}</code>
              </td>
              <td>
                <StatusBadge status={w.status} />
                {w.lastError && <div className="small err" title={w.lastError}>attempt {w.attempts}: {w.lastError.slice(0, 80)}</div>}
              </td>
              <td className="opt">{w.schemaVersion ? `v${w.schemaVersion}` : "—"}</td>
              <td><When at={w.requestedAt} /></td>
              <td className="row-actions">
                <WorkspaceActions w={w} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {list.isLoading && <p className="muted">Loading…</p>}
    </>
  );
}

